import React from 'react';
import styled from 'styled-components';
import { TriageCategory } from '../../../../graphQL';
import { humanizeEnumValue } from '../../../../utils/humanizeEnumValue';
import { colors } from '../../../../globalStyles';
import { SaiStudentTriage } from '../saiStudentDetail.types';
import {
  CATEGORY_LABELS,
  TRIAGE_DRIFTING_ACTIVE_COLOR,
  TRIAGE_DRIFTING_MARKER_COLOR,
  TRIAGE_HIGH_NEED_ACTIVE_COLOR,
  TRIAGE_HIGH_NEED_MARKER_COLOR,
  TRIAGE_STABLE_ACTIVE_COLOR,
  TRIAGE_STABLE_MARKER_COLOR,
  TRIAGE_STRAINED_ACTIVE_COLOR,
  TRIAGE_STRAINED_MARKER_COLOR,
} from '../../../../Components/Beacon/constants';
import { TriageMiniMatrix } from './TriageMiniMatrix';

// Matrix cells run top-left, top-right, bottom-left, bottom-right. Positions and the
// per-category color pairs come from the product mock: Drifting top-left, Stable top-right,
// High Need bottom-left, Strained bottom-right.
const CATEGORY_STYLES: Record<
  TriageCategory,
  { cell: number; activeColor: string; markerColor: string }
> = {
  HighNeed: {
    cell: 2,
    activeColor: TRIAGE_HIGH_NEED_ACTIVE_COLOR,
    markerColor: TRIAGE_HIGH_NEED_MARKER_COLOR,
  },
  Strained: {
    cell: 3,
    activeColor: TRIAGE_STRAINED_ACTIVE_COLOR,
    markerColor: TRIAGE_STRAINED_MARKER_COLOR,
  },
  Drifting: {
    cell: 0,
    activeColor: TRIAGE_DRIFTING_ACTIVE_COLOR,
    markerColor: TRIAGE_DRIFTING_MARKER_COLOR,
  },
  Stable: {
    cell: 1,
    activeColor: TRIAGE_STABLE_ACTIVE_COLOR,
    markerColor: TRIAGE_STABLE_MARKER_COLOR,
  },
};

export const TriageCategoryBadge = ({ triage }: { triage: SaiStudentTriage }) => {
  // A category this build does not know can still arrive from a newer API, so the badge falls back
  // to the raw value and drops the matrix rather than rendering an empty cell.
  const label = CATEGORY_LABELS[triage.category] ?? humanizeEnumValue(triage.category);
  const style = CATEGORY_STYLES[triage.category] ?? null;

  return (
    <BadgeRowDiv>
      {style && (
        <TriageMiniMatrix
          activeCell={style.cell}
          activeColor={style.activeColor}
          markerColor={style.markerColor}
          size={56}
        />
      )}
      <BadgeTextDiv>
        <EyebrowSpan>Triage Category</EyebrowSpan>
        <CategoryLabelSpan $color={style?.markerColor ?? colors.text}>{label}</CategoryLabelSpan>
      </BadgeTextDiv>
    </BadgeRowDiv>
  );
};

const BadgeRowDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const BadgeTextDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EyebrowSpan = styled.span`
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const CategoryLabelSpan = styled.span<{ $color: string }>`
  color: ${props => props.$color};
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
`;
